import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getDashboardSummary, getMarketData } from '../api';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Wallet, CreditCard, DollarSign, Building2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color }) => (
  <Card className="card-hover border-slate-200">
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm ${
            trend === 'up' ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-sm text-slate-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-slate-900" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = ({ user, onLogout }) => {
  const [summary, setSummary] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, marketRes] = await Promise.all([
        getDashboardSummary(),
        getMarketData()
      ]);
      setSummary(summaryRes.data);
      setMarketData(marketRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="dashboard-title">
            Dashboard Overview
          </h1>
          <p className="text-slate-600">Monitor your fund flow and financial position</p>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-12 w-12 rounded-xl mb-4" />
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Balance"
              value={formatCurrency(summary?.total_balance || 0)}
              icon={Wallet}
              color="blue"
            />
            <StatCard
              title="Usable Balance"
              value={formatCurrency(summary?.usable_balance || 0)}
              icon={DollarSign}
              color="green"
            />
            <StatCard
              title="Pending Receipts"
              value={formatCurrency(summary?.pending_cheques || 0)}
              icon={CreditCard}
              color="orange"
            />
            <StatCard
              title="Upcoming Payments"
              value={formatCurrency(summary?.upcoming_payments || 0)}
              icon={TrendingDown}
              color="red"
            />
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
              <CardDescription>Your business overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-slate-600" />
                    <span className="text-slate-700">Companies</span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900" data-testid="companies-count">{summary?.companies_count || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-slate-600" />
                    <span className="text-slate-700">Bank Accounts</span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900" data-testid="banks-count">{summary?.banks_count || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Fund Flow Status</CardTitle>
              <CardDescription>Current financial position</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Available Funds</span>
                    <span className="font-semibold text-green-600">Healthy</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-green-600"
                      style={{
                        width: `${Math.min((summary?.usable_balance / summary?.total_balance) * 100, 100) || 0}%`
                      }}
                    />
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-800 mb-1">Cash Flow Insight</p>
                  <p className="text-xs text-blue-600">
                    {summary?.usable_balance > summary?.upcoming_payments
                      ? 'Your usable balance exceeds upcoming payments. Consider short-term investments.'
                      : 'Upcoming payments are high. Monitor cash position closely.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Market Data</CardTitle>
              <CardDescription>Currency conversion rates</CardDescription>
            </CardHeader>
            <CardContent>
              {marketData ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-slate-600" />
                      <span className="text-slate-700">USD to INR</span>
                    </div>
                    <span className="text-xl font-semibold text-slate-900" data-testid="usd-inr-rate">
                      ₹{marketData.usd_inr?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-slate-600" />
                      <span className="text-slate-700">Gold Price</span>
                    </div>
                    <span className="text-xl font-semibold text-slate-900" data-testid="gold-price">
                      ₹{marketData.gold_price?.toFixed(0) || 'N/A'}/10g
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full rounded-lg" />
                  <Skeleton className="h-16 w-full rounded-lg" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;